import Link from 'next/link'
import type React from 'react'
import Tracking from '../assets/app.svg'
import Calculator from '../assets/calculator.svg'
import Compassion from '../assets/compassion.svg'
import Fitness from '../assets/fitness.svg'
import HealthyFoods from '../assets/healthyfoods.svg'
import Kitchen from '../assets/kitchen.svg'
import Broccoli from '../assets/lined-broccoli.svg'
import Protein from '../assets/protein-powder.svg'
import Tape from '../assets/scale.svg'
import Sleep from '../assets/sleep.svg'
import Target from '../assets/target.svg'
import TV from '../assets/tv.svg'
import Wave from '../assets/wave.svg'
import {
  MultivitaminForMen,
  MultivitaminForWomen,
} from '../components/affiliates/WeightLoss'
import { Footnote } from '../components/curriculum/Footnote'
import { FootnoteList } from '../components/curriculum/FootnoteList'
import { Explanation } from '../components/explanation/Explanation'
import { Section } from '../components/guides/Section'
import { Title } from '../components/guides/Title'
import { Layout } from '../components/Layout'

export const ImplementationIntentions: React.FC<{
  title?: string
  hideTip: boolean
}> = ({ hideTip, title }) => {
  return (
    <Section>
      <Title
        alt="Target"
        src={Target}
        title={title || 'Set a regular meal time'}
      />
      <Explanation color="blue">
        <div>
          <strong>Insight:</strong> Motivation is useless for almost everything,
          but especially weight loss and exercise. The evidence is clear and
          overwhelming.{' '}
          <FootnoteList
            links={[
              'https://jamesclear.com/implementation-intentions',
              'https://journals.lww.com/ebp/Citation/2020/10000/Is_motivational_interviewing_effective_for_weight.17.aspx',
            ]}
          />
          . The only mental technique that is actually works is an{' '}
          <strong>implementation intention</strong>. This is when you set{' '}
          <strong>when</strong> and <strong>where</strong> you are going to
          doing something. For example, I will exercise at 3 PM at Tuesday. Or I
          will eat every day at 11:00 AM, 3:00 PM, and 7:00 PM for a maximum of
          30 minutes. While this technique may seem trivial, it will double the
          odds of you sticking to your plan. For example, it may help you
          eliminate eating at night.
        </div>
      </Explanation>
      {!hideTip && (
        <Explanation color="green">
          <div>
            <strong>Tip: </strong> The other benefit of setting a meal time is
            you get <strong>regular eating</strong>. This is when you try to eat
            a meal or snack every four hours or so. Researchers have shown this
            eliminates the two, major causes of <strong>binge eating</strong>,
            delayed eating and dietary restriction{' '}
            <Footnote link="https://3dmusclejourney.com/binge-eating-causing-can-overcome/" />
            . This also explains why restrictive diets, like keto, and
            intermittent fasting diets, like 16:8, are terrible for those with
            the binge eating disorder.
          </div>
        </Explanation>
      )}
    </Section>
  )
}

const WeightLoss = () => {
  return (
    <Layout>
      <div className={'w90 center module'}>
        <h1 className="huge">Weight loss</h1>
        <Explanation color="blue">
          <div>
            <strong>Insight: </strong>Weight loss is hard. These techniques make
            it easier. This guide{' '}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://youtu.be/AxMCnDvgY-c"
            >
              is a video as well.
            </a>{' '}
          </div>
        </Explanation>
        <div>
          <h2>Foundation</h2>
          <Section>
            <Title
              alt="Smiley face"
              src={Compassion}
              title={'Embrace self compassion'}
            />
            <Explanation color="blue">
              <div>
                <strong>Insight:</strong> The secret to weight loss isn{`'`}t
                perfectionism. How could it be? Weight loss is{' '}
                <strong>really</strong> hard, and shaming or criticizing
                yourself, especially after a mistake, will only make you feel
                worse and eat more. But there is a kinder and more effective way
                to lose weight, grounded in two ideas{' '}
                <FootnoteList
                  links={[
                    'https://self-compassion.org/wp-content/uploads/2019/08/Ferrari2019.pdf',
                    ' https://journals.sagepub.com/doi/10.1177/1359105316686668',
                  ]}
                />
                . The first is a willingness to{' '}
                <strong>forgive yourself and to try again</strong>. The second
                is a willingness to take pride in yourself, no matter where you
                are on your weight loss journey.
              </div>
            </Explanation>
            <Explanation color="green">
              <div>
                <strong>Why this works: </strong>
                Let’s say you wanted to eat 1,000 calories every day, but only
                managed to hit that target on 4 of 7 days. Well, you still
                accrued a deficit of 4,000 calories over the week, and thus lost
                over a pound of fat. Thus, you ultimately succeeded,{' '}
                <strong>even though there were days that you failed</strong> ,
                because you were willing to be imperfect and try again.
              </div>
            </Explanation>
          </Section>

          <h2>Food</h2>
          <Section>
            <Title
              alt="Calculator"
              src={Calculator}
              title={'Calories over diet'}
            />
            <Explanation color="blue">
              <div>
                <strong>Insight:</strong> So much weight loss advice is centered
                around a specific <strong>diet</strong>, from keto to vegan. But
                that is a mistake. Assuming you eat the same number of calories
                per day, you{`'`}ll lose the same amount of weight on any diet{' '}
                <FootnoteList
                  links={[
                    'https://www.sciencedirect.com/science/article/abs/pii/0026049564900988',
                    'https://pubmed.ncbi.nlm.nih.gov/26278052/',
                  ]}
                />
                . Yes, even a Twinkie diet{' '}
                <Footnote link="https://www.cnn.com/2010/HEALTH/11/08/twinkie.diet.professor/index.html" />
                . All that matters for weight loss is calculating how many
                calories you need to eat per day to maintain your current
                weight, called your{' '}
                <Link href="/caloric maintenance">caloric maintenance</Link>,
                and then eating less than that, AKA at a deficit. For example,
                if you eat 500 below your maintenance per day, you{`'`}ll lose a
                pound of fat per week, as one pound of fat is 3,500 calories.
              </div>
            </Explanation>
            <Explanation color="green">
              <div>
                <strong>Tip:</strong> Need to calculate your caloric
                maintenance?{' '}
                <a href="https://www.calculator.net/bmr-calculator.html">
                  This calculator
                </a>{' '}
                does a good job. Our app also has an integrated calculator that
                does all of this math for you.
              </div>
            </Explanation>
          </Section>
          <Section>
            <Title
              alt="Caloric density"
              src={Broccoli}
              title={'Embrace caloric density'}
            />
            <Explanation color="blue">
              <div>
                <strong>Insight:</strong> The more a food weighs, the better it
                is at fighting hunger, even when calories are constant. This is
                why eating 100 calories of Twizzlers (~ 3 Twizzlers) will leave
                you hungry while eating 100 calories of broccoli (~ 1 lb) will
                leave you super full. Thus, eating foods low in{' '}
                <strong>caloric density</strong>, a number that measures that
                calorie to weight relationship, is a super effective way to diet{' '}
                <FootnoteList
                  links={[
                    'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4182946/',
                    'https://pubmed.ncbi.nlm.nih.gov/15976148/',
                    'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2018610/',
                    'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2741457/',
                  ]}
                />
                , especially because hunger is the number one predictor of
                weight loss success{' '}
                <Footnote link="https://pubmed.ncbi.nlm.nih.gov/23512619/" />.
              </div>
            </Explanation>
            <Explanation color="green">
              <div>
                <strong>Tip: </strong>You can see our recommended foods{' '}
                <Link href="/groceries">here</Link> and a full list of foods
                ranked by caloric density in our app. Anything with a caloric
                density below 40, usually a vegetable, is great a good choice.
              </div>
            </Explanation>
            <Explanation color="yellow">
              <div>
                <strong>Heads up: </strong>While drinks like soda and juice have
                a low caloric density, due to their high water content, they do
                not fight hunger at all{' '}
                <Footnote link="https://pubmed.ncbi.nlm.nih.gov/15808893/" />.
                This makes them <strong>terrible</strong> for weight loss,
                confirming the old adage {`"`}never drink your calories{`"`}.
                The exceptions to this rule are soups and diet soda, the latter
                of which makes it easier to switch away from {`"`}real{`"`} soda{' '}
                <Footnote link="https://www.ncbi.nlm.nih.gov/pubmed/26365102" />
                .
              </div>
            </Explanation>
          </Section>

          <Section>
            <Title
              alt="Protein density"
              src={Protein}
              title={'Embrace protein density'}
            />
            <Explanation color="blue">
              <div>
                <strong>Insight:</strong> Of the three macronutrients,
                carbohydrates, protein, and fat, protein is by far the most
                filling{' '}
                <FootnoteList
                  links={[
                    'https://pubmed.ncbi.nlm.nih.gov/16002798/',
                    'https://pubmed.ncbi.nlm.nih.gov/17622289/',
                  ]}
                />
                . In one study, simply replacing 15% of the calories from fat
                with protein led to people eating 500 fewer calories per day
                without trying{' '}
                <Footnote link="https://pubmed.ncbi.nlm.nih.gov/16002798" />.
                Protein density is also really important for building and
                preserving muscle, especially during a diet{' '}
                <Footnote link="https://mennohenselmans.com/the-myth-of-1glb-optimal-protein-intake-for-bodybuilders/" />
                . The best way to measure protein content is{' '}
                <strong>protein density</strong>, the percentage of a food{`'`}s
                calories that come from protein. For example, chicken breast has
                a protein density of 75, while strawberries only have a protein
                density of 9.
              </div>
            </Explanation>
            <Explanation color="green">
              <div>
                <strong>Tip: </strong>You can see our recommended foods{' '}
                <Link href="/groceries">here</Link> and a full list of foods
                ranked by protein density{' '}
                <a
                  target="_blank"
                  href="https://app.cleanslate.sh/foods"
                  rel="noopener noreferer noreferrer"
                >
                  here
                </a>
                . Anything with a protein density above 70 is usually a good
                choice.
              </div>
            </Explanation>
            <Explanation color="yellow">
              <div>
                <strong>Heads up: </strong>Once the hunger-fighting effect of
                protein {`"`}top outs{`"`} at ~ 1.8 kg per gram{' '}
                <Footnote link="https://mennohenselmans.com/protein-satiety-study/" />
                , it becomes a question of carb vs. fat. We recommend carb, as
                studies show it{`'`}s better at fighting hunger. For example, in
                one study, participants consumed 990 more calories per day on
                HFLC (high-fat, low-carb) than HCLF (high-carb, low-fat){' '}
                <Footnote link="https://examine.com/members/deep-dives/article/high-carb-high-satiety/" />
                , while in another study, a HCLF with low caloric density
                outperformed HFLC by ~ 600 calories per day, even though the
                HFLC participants were in ketosis{' '}
                <Footnote link="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC7258127/" />
                .
              </div>
            </Explanation>
          </Section>
          <Section>
            <Title
              alt="Healthy eating"
              src={HealthyFoods}
              title={'Eat healthily'}
            />
            <Explanation color="blue">
              <div>
                <strong>Insight:</strong> Healthy eating is rather simple. Was
                the food made in a factory? Well, then it{`'`}s usually
                unhealthy. Otherwise, you
                {`'`}re golden. And yes, this covers everything from beans, to
                fruit, to red meat. The evidence is quite clear that our
                ancestors were both quite healthy and ate a massive variety of
                different foods{' '}
                <Footnote link="https://www.nytimes.com/2018/12/18/well/eat/is-there-an-optimal-diet-for-humans.html" />
              </div>
            </Explanation>
            <Explanation color="green">
              <div>
                <strong>Tip: </strong>
                Eating healthy has an added bonus: it{`'`}s really good at
                fighting hunger, even after controlling for stuff like fiber,
                caloric and protein density, and more{' '}
                <Footnote link="https://www.cell.com/cell-metabolism/fulltext/S1550-4131(19)30248-7" />
              </div>
            </Explanation>
          </Section>
          <Section>
            <Title alt="Kitchen" src={Kitchen} title={'Change your kitchen'} />
            <Explanation color="blue">
              <div>
                <strong>Insight:</strong> Humans will basically eat whatever
                food is <strong>available</strong>, even stale popcorn{' '}
                <Footnote link="https://news.cornell.edu/stories/2005/11/big-portions-influence-overeating-much-taste-even-when-food-tastes-lousy-cornell" />
                . Even something as simple as moving a bowl of chocolates closer
                and making that bowl clear more than doubles consumption{' '}
                <Footnote link="https://pubmed.ncbi.nlm.nih.gov/16418755/" />.
                So, if you keep healthy, low-density foods like broccoli and
                salmon in the house, and remove junk food like chips and soda,
                your default meal becomes much better. Plus, you{`'`}ll find
                yourself craving those junk foods less as you eat them less,
                even in a caloric deficit{' '}
                <Footnote link="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6226249/" />
                .{' '}
                <strong>Just make sure you enjoy your replacement foods</strong>
                , otherwise your deficit won{`'`}t be sustainable.
              </div>
            </Explanation>
            <Explanation color="green">
              <div>
                <strong>Tip:</strong> When you remove a food from the house,
                like an Oreo, that does not mean it is banned. After all, when
                you set black-and-white rules, like, {`"`}I will never eat an
                Oreo{`"`}, or {`"`}I will never eat past 7{`"`}, that only
                increases the reward value of that food in your brain,
                strengthening the feeling of psychological hunger and making the
                food harder to resist{' '}
                <Footnote link="https://en.wikipedia.org/wiki/Ironic_process_theory" />
                . Instead, just think of it as removing the food from your
                environment most of the time, making your life a little easier.
              </div>
            </Explanation>
            <Explanation color="background">
              <div>
                <strong>Interesting: </strong>
                The obesity epidemic was not caused by set points, genetics{' '}
                <Footnote link="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3209643/" />
                , or a lack of exercise, which has remained constant since the
                1980s. Instead, the obesity epidemic is due to an increase in
                calories consumed, as certain foods, like cake, became more{' '}
                <strong>available</strong> due to the mass production of food{' '}
                <Footnote link="https://www.brown.edu/Research/Shapiro/pdfs/obesity.pdf" />
                . The ability to buy a massive cake from Costco for $20 is a
                blessing and a curse!
              </div>
            </Explanation>
          </Section>

          <h2>Measurement</h2>

          <Section>
            <Title alt="App" src={Tracking} title={'Track what you eat'} />
            <Explanation color="blue">
              <div>
                <strong>Insight: </strong>People are terrible at measuring how
                many calories they eat per day. In fact, for some, this
                measurement error can <strong>exceed 50%</strong>{' '}
                <FootnoteList
                  links={[
                    'https://www.theatlantic.com/health/archive/2012/03/why-calories-count-the-problem-with-dietary-intake-studies/254886/',
                    'https://www.sciencedirect.com/science/article/abs/pii/0026049564900988',
                  ]}
                />
                . That’s like thinking you’re eating 1,000 calories but actually
                eating 1,500 calories, which is the difference between losing
                weight and maintaining it. This inaccuracy, which we call a lack
                of caloric awareness, can be remedied by using a food tracker{' '}
                <Footnote link="https://pubmed.ncbi.nlm.nih.gov/24636238/" />.
                In fact, this is what Clean Slate is: a food tracker designed
                for people who struggle with perfectionism.
              </div>
            </Explanation>
            <Explanation color="green">
              <div>
                <strong>Tip:</strong> You don{`'`}t have to track forever. After
                a few weeks, you{`'`}ll have a rough idea of how many calories
                you eat per day,{' '}
                <strong>assuming you eat similar stuff each week.</strong> You
                also don{`'`}t need to track at the occasional restaurant,
                party, etc. as these events are about enjoyment, not calorie
                counts. Basically, as long as you know how many calories you
                {`'`}re eating about 90% of the time, you{`'`}re golden.
              </div>
            </Explanation>
            <Explanation color="yellow">
              <div>
                <strong>Heads up:</strong> We don{`'`}t track carbohydrates,
                fat, fiber, vitamins, or minerals. This is because only calories
                and protein matter for hunger and weight loss, fat and
                carbohydrates are equally healthy, and you can meet your vitamin
                and mineral requirements with a multivitamin for like 5 cents
                per day. We like <MultivitaminForMen text="this one" /> for men
                and <MultivitaminForWomen text="this one" /> for women.
              </div>
            </Explanation>
          </Section>

          <Section>
            <Title
              alt="Measuring tape"
              src={Tape}
              title={'Use the scale carefully'}
            />
            <Explanation color="blue">
              <div>
                <strong>Insight:</strong> While measuring your fat loss is
                important{' '}
                <FootnoteList
                  links={[
                    'https://pubmed.ncbi.nlm.nih.gov/32210897/',
                    'https://pubmed.ncbi.nlm.nih.gov/24355668/',
                  ]}
                />
                . the scale is a tricky way to do so! This is because your
                weight can fluctuate up to 5 pounds every day, mostly due to
                changes in water retention{' '}
                <Footnote link="https://www.healthline.com/health/weight-fluctuation" />
                , especially on your period. Hence, while we do recommend
                weighing yourself every day or week to track your progress, just
                keep it mind two things when doing so. One, your weight may go
                up on that day for a reason outside of your control. Two, as
                long as your weight is decreasing from month to month, you are
                golden!
              </div>
            </Explanation>
          </Section>

          <h2>Behavior</h2>

          <ImplementationIntentions hideTip={false} />

          <Section>
            <Title alt="TV" src={TV} title={`Don't eat or cook with TV`} />
            <Explanation color="blue">
              <div>
                <strong>Insight: </strong> Eating or cooking with TV
                unequivocally increases energy intake{' '}
                <FootnoteList
                  links={[
                    'https://sci-hub.se/https://pubmed.ncbi.nlm.nih.gov/24462489/',
                    'https://pubmed.ncbi.nlm.nih.gov/17284729/',
                    'https://pubmed.ncbi.nlm.nih.gov/26921486',
                    'https://www.nature.com/articles/pr2007158',
                  ]}
                />
                . This is for two reasons. One, TV will extend the time period
                you eat to the length of the show. Two, TV will force you to
                miss food cues (i.e. mindless eating), which are important for
                satiety. In other words, when you cook or eat with TV, you eat
                for longer and it takes more food to fill you up.
              </div>
            </Explanation>
          </Section>

          <Section>
            <Title alt="Wave" src={Wave} title={'Learn to urge surf'} />
            <Explanation color="blue">
              <div>
                <strong>Insight: </strong> Psychological hunger is the desire to
                eat when your body doesn{`'`}t need the nutrients. Unlike real
                hunger, which comes on gradually and came be satisfied with any
                food, psychological hunger comes on suddenly, usually with an
                emotion, like boredom, or a specific food, like chocolate. It
                can result in everything from overeating to binge eating. To
                handle it, we recommend <strong>urge surfing</strong>, a highly
                effective mindfulness technique.
                <FootnoteList
                  links={[
                    'https://pubmed.ncbi.nlm.nih.gov/22114078/',
                    'https://pubmed.ncbi.nlm.nih.gov/17544361/',
                  ]}
                />{' '}
                When you{`'`}re ready to learn it, check out{' '}
                <Link href="/urge-surfing">our guide</Link>
              </div>
            </Explanation>
          </Section>

          <Section>
            <Title alt="Exercise" src={Fitness} title={'Consider exercising'} />
            <Explanation color="blue">
              <div>
                <strong>Insight:</strong> Exercise is not magic. 1,000 calories
                burned from exercise will have the same effect as 1,000 calories
                burned from dietary restriction{' '}
                <FootnoteList
                  links={[
                    'https://pubmed.ncbi.nlm.nih.gov/27465384/',
                    'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4896218/',
                  ]}
                />
                . But, still, it{`'`}s <strong>stupidly</strong> good for your
                overall health{' '}
                <FootnoteList
                  links={[
                    'http://www.aomrc.org.uk/wp-content/uploads/2016/05/Exercise_the_Miracle_Cure_0215.pdf',
                    'https://www.bmj.com/content/366/bmj.l4570',
                  ]}
                />
                and useful if you want to burn some extra calories{' '}
                <Footnote link="https://www.ncbi.nlm.nih.gov/pmc/articles/pmid/33255753/" />
                . It{`'`}s also the only way to get muscular, the core driver of
                sex appeal in <strong>both</strong> men{' '}
                <Footnote link="https://bonytobeastly.com/ectomorph-aesthetics-full-article/" />{' '}
                and women{' '}
                <Footnote link="https://bonytobombshell.com/bombshell-aesthetics-building-attractive-female-body-imaginable/" />
                .
              </div>
            </Explanation>
            <Explanation color="green">
              <div>
                <strong>Tip: </strong>Start with our{' '}
                <Link href="/lifting">Guide to Lifting</Link>. Then, if you want
                to burn some extra calories, check out the{' '}
                <Link href="/cardio">Guide to Cardio</Link>, as you{`'`}ll find
                incline treadmill walking and the elliptical can burn a
                staggering amount of calories without having to run.
              </div>
            </Explanation>
          </Section>

          <Section>
            <Title alt="Sleep" src={Sleep} title={'Improve your sleep'} />
            <Explanation color="blue">
              <div>
                <strong>Insight:</strong> Having proper sleep is important for
                weight loss. Not only does sleep deprivation make you hungrier,
                it also increases how much muscle you lose during a diet{' '}
                <FootnoteList
                  links={[
                    'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2951287/',
                    'https://www.facebook.com/MennoHenselmans/posts/2984166111641204&#39;',
                  ]}
                />
                . Plus, sleep deprivation is also linked to basically every bad
                health outcome, especially depression, so it’s worth fixing{' '}
                <Footnote link="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5449130/" />
                .
              </div>
            </Explanation>
            <Explanation color="green">
              <div>
                <strong>Tip: </strong> We cover exactly how to improve your
                sleep in our <Link href="/sleep">Guide to Sleep</Link>.
              </div>
            </Explanation>
          </Section>
        </div>
      </div>
    </Layout>
  )
}

export default WeightLoss
